import type { ReminderAction, ReminderNotice, ReminderPlan, SnoozeChoice } from '../types/reminders'
import { dueNotices, nextCheckIn, pruneFired, snoozeKey, snoozeUntil } from './reminderPlan'

/*
  **Der Zeitgeber der Zustellung** (Plan task-reminders, Etappen 3 und 4) — für Desktop und Web.

  Er kennt keine Aufgaben (ADR 0025): Der Renderer schickt ihm den fertigen Plan. Er merkt sich,
  was schon klang, holt Verpasstes nach und legt Aktionen aus einer Mitteilung (Erledigen, Später,
  Klick) in eine Warteschlange, die der Renderer abholt. Am Desktop lebt er im Main-Prozess, damit
  er auch klingelt, wenn kein Fenster mehr lebt (unter macOS wird das Hauptfenster beim Schließen
  zerstört); im Web im Tab.

  Gebaut über Abhängigkeiten statt direkter Plattform-Aufrufe — so lässt sich alles ohne Fenster
  und mit einer gestellten Uhr prüfen. Verdrahtet in `src/main/reminders/index.ts` und
  `src/renderer/lib/platform/webReminders.ts`.
*/

export interface StoredReminderState {
  notices: ReminderNotice[]
  /** Schlüssel → wann er klang. */
  fired: Record<string, number>
  lastCheckedAt: number | null
  /**
   * Aktionen, die der Renderer noch nicht abgeholt hat — gesichert, weil sie einen Neustart
   * überstehen müssen: „Später“ bei geschlossenem Fenster, dann die App beendet, und der nächste
   * Plan vom Renderer (ohne `snoozedUntil`) hätte den Schlummer-Termin gelöscht.
   */
  actions?: ReminderAction[]
}

export interface ShownNotification {
  title: string
  body: string
  /** Knöpfe der Mitteilung, nur macOS: Erledigen, dann die Schlummer-Stufen. */
  actions: string[]
}

export interface ReminderSchedulerDeps {
  now: () => number
  load: () => StoredReminderState | null
  save: (state: StoredReminderState) => void
  show: (notification: ShownNotification, handlers: { onClick: () => void; onAction: (index: number) => void; onClose: () => void }) => { close: () => void }
  /** „Auf diesem Gerät erinnern“. */
  isEnabled: () => boolean
  /** Nur macOS kennt Knöpfe an der Mitteilung; unter Windows bräuchten sie eigenes Toast-XML. */
  withActions: boolean
  text: (key: string, params?: Record<string, string | number>) => string
  /** Hauptfenster zeigen — oder anlegen, wenn keins lebt. */
  revealMainWindow: () => void
  hasMainWindow: () => boolean
  /** Sagt jedem Hauptfenster, dass Aktionen warten. */
  notifyRenderers: () => void
  setTimer: (run: () => void, ms: number) => unknown
  clearTimer: (timer: unknown) => void
}

/** Reihenfolge der Knöpfe nach „Erledigen“. */
export const SNOOZE_CHOICES: readonly SnoozeChoice[] = ['15m', '1h', '3h', 'tomorrow']
/** Nicht mehr offene Mitteilungen halten wir nicht ewig fest — sie verschwinden ohnehin aus der Zentrale. */
const MAX_SHOWN = 50

const clock = (ms: number) => {
  const d = new Date(ms)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export function createReminderScheduler(deps: ReminderSchedulerDeps) {
  const state: StoredReminderState = deps.load() ?? { notices: [], fired: {}, lastCheckedAt: null }
  state.actions ??= []
  let timer: unknown = null
  /**
   * Gezeigte Mitteilungen samt ihrer Aufgabe. Die Referenz muss leben, solange die Mitteilung
   * steht — sonst räumt der Garbage Collector sie ab und ein Klick kommt nie an.
   */
  const shown = new Map<string, { taskId: string | null; close: () => void }>()

  const firedKeys = () => new Set(Object.keys(state.fired))
  const persist = () => deps.save(state)

  function queue(action: ReminderAction) {
    state.actions = [...(state.actions ?? []), action]
    persist()
    deps.notifyRenderers()
  }

  function forget(key: string) {
    shown.get(key)?.close()
    shown.delete(key)
  }

  function present(key: string, taskId: string | null, notification: ShownNotification, onAction?: (index: number) => void) {
    if (shown.size >= MAX_SHOWN) forget(shown.keys().next().value!)
    const handle = deps.show(notification, {
      onClick: () => {
        forget(key)
        queue({ kind: 'open', taskId })
        deps.revealMainWindow()
      },
      onAction: index => {
        forget(key)
        onAction?.(index)
      },
      onClose: () => shown.delete(key),
    })
    shown.set(key, { taskId, close: handle.close })
  }

  function presentNotice(notice: ReminderNotice, missed: boolean) {
    const body = missed ? `${deps.text('reminders.notification.missedPrefix', { time: clock(notice.fireAt) })} · ${notice.body}` : notice.body
    const actionLabels = deps.withActions
      ? [deps.text('reminders.notification.complete'), ...SNOOZE_CHOICES.map(choice => deps.text(`reminders.notification.snooze.${choice}`))]
      : []
    present(notice.key, notice.taskId, { title: notice.title, body, actions: actionLabels }, index => handleAction(notice, index))
  }

  function handleAction(notice: ReminderNotice, index: number) {
    if (index === 0) {
      queue({ kind: 'complete', taskId: notice.taskId })
      // Die Datenschicht lebt im Renderer: Ist keiner da (macOS, Fenster zu), kommt das Fenster.
      if (!deps.hasMainWindow()) deps.revealMainWindow()
      return
    }
    const choice = SNOOZE_CHOICES[index - 1]
    if (!choice) return
    const until = snoozeUntil(choice, new Date(deps.now())).getTime()
    // Sofort in den eigenen Plan — so klingelt es auch, wenn der Renderer erst später kommt. Wie
    // der Zeitplan (`computeReminderSchedule`): Reguläre Termine dieser Erinnerung bis einschließlich
    // `until` entfallen, sonst klingelte „Morgen um 9:00“ an einer 9-Uhr-Erinnerung zweimal.
    const key = snoozeKey(notice.reminderId, until)
    state.notices = [
      ...state.notices.filter(n => n.key !== key && !(n.reminderId === notice.reminderId && n.fireAt <= until)),
      { ...notice, key, fireAt: until },
    ]
    persist()
    queue({ kind: 'snooze', reminderId: notice.reminderId, until })
    arm()
  }

  function check() {
    const now = deps.now()
    const { onTime, missed, expired } = dueNotices(state.notices, firedKeys(), state.lastCheckedAt, now)
    for (const notice of [...onTime, ...missed, ...expired]) state.fired[notice.key] = now
    state.fired = pruneFired(state.fired, now)
    state.lastCheckedAt = now
    persist()
    if (!deps.isEnabled()) return
    for (const notice of onTime) presentNotice(notice, false)
    if (missed.length === 1) presentNotice(missed[0]!, true)
    if (missed.length > 1) {
      present(`missed@${now}`, null, {
        title: deps.text('reminders.notification.missedTitle', { count: missed.length }),
        body: missed.map(n => `${clock(n.fireAt)} ${n.title}`).join(' · '),
        actions: [],
      })
    }
  }

  function arm() {
    if (timer !== null) deps.clearTimer(timer)
    timer = deps.setTimer(() => {
      timer = null
      check()
      arm()
    }, nextCheckIn(state.notices, firedKeys(), deps.now()))
  }

  return {
    /** Beim Start: Verpasstes aus dem gespeicherten Plan nachholen, dann den Zeitgeber stellen. */
    start() {
      check()
      arm()
    },
    /** Nach Schlaf oder Entsperren — die Zeit ist gesprungen. */
    wake() {
      check()
      arm()
    },
    /**
     * Der neue Plan vom Renderer. Erst der alte bis jetzt — dann gilt der neue ab jetzt: Ein Termin,
     * der schon vorbei war, als er angelegt wurde, klingelt nicht nachträglich.
     */
    setPlan(plan: ReminderPlan) {
      check()
      state.notices = plan.notices
      state.lastCheckedAt = deps.now()
      persist()
      const open = new Set(plan.openTaskIds)
      for (const [key, entry] of shown) {
        if (entry.taskId && !open.has(entry.taskId)) forget(key)
      }
      arm()
    },
    /** Die wartenden Aktionen — und die Warteschlange ist danach leer (mehrere Fenster holen nur einmal). */
    takeActions(): ReminderAction[] {
      const taken = state.actions ?? []
      if (taken.length === 0) return []
      state.actions = []
      persist()
      return taken
    },
    /**
     * **Alles vergessen** — beim Abmelden: Plan, Gemerktes, Warteschlange; was gerade steht, geht zu.
     * Ohne das klängen die Erinnerungen des alten Kontos weiter, und ein neues holte sie als
     * „verpasst“ nach.
     */
    clear() {
      for (const key of [...shown.keys()]) forget(key)
      state.notices = []
      state.fired = {}
      state.actions = []
      state.lastCheckedAt = deps.now()
      persist()
      arm()
    },
    test() {
      present(`test@${deps.now()}`, null, { title: deps.text('reminders.notification.testTitle'), body: deps.text('reminders.notification.testBody'), actions: [] })
    },
    stop() {
      if (timer !== null) deps.clearTimer(timer)
      timer = null
    },
  }
}

export type ReminderScheduler = ReturnType<typeof createReminderScheduler>
